/**
 * Faculty Dashboard — the KAPE landing page.
 * Route: /kcis/admin/author-workspace
 *
 * Replaces the earlier "My Courses" grid with a segmented view:
 *   Drafts / Generating / Needs Review / Needs Narration Approval /
 *   Needs Publishing / Published / Podcast Ready
 * + Pending Jobs strip + Pending Approvals strip.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";
import PreviewAsStudentButton from "./PreviewAsStudentButton";

const SEGMENT_ORDER = [
  ["needs_publishing",         "Needs Publishing",         "#10B981"],
  ["needs_narration_approval", "Needs Narration Approval", "#F59E0B"],
  ["needs_review",             "Needs Review",             "#F59E0B"],
  ["generating",               "Generating",               "#3B82F6"],
  ["drafts",                   "Drafts",                   "#6B7280"],
  ["published",                "Published",                "#10B981"],
];

export default function FacultyDashboard() {
  const [data, setData] = useState(null);
  const [profiles, setProfiles] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        api.get("/kcis/admin/kape/faculty-dashboard"),
        api.get("/kcis/admin/instructor-profiles"),
      ]);
      setData(d.data);
      setProfiles(p.data.items || []);
    } catch {
      setData({ segments: {}, pending_jobs: [], total: 0 });
    }
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  const podcastReady = Object.values(data?.segments || {}).flat().filter(c => c.podcast_ready);

  return (
    <KcisAdminShell>
      <div className="max-w-7xl mx-auto px-8 py-10" data-testid="faculty-dashboard">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KCIS · University · Author Workspace
            </div>
            <h1 className="text-3xl font-semibold mt-1">Faculty Dashboard</h1>
            <p className="mt-2 max-w-2xl text-sm" style={{ color: PAL.muted }}>
              Every course you author, grouped by what needs your attention next.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/kcis/admin/job-center"
                  className="rounded-full px-3 py-2 text-xs border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid="fd-open-job-center">
              Job Center ({data?.pending_jobs?.length || 0} active)
            </Link>
            <Link to="/kcis/admin/instructor-profiles"
                  className="rounded-full px-3 py-2 text-xs border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid="fd-open-instructor-profiles">
              Instructor Profiles ({profiles.length})
            </Link>
            <Link to="/kcis/admin/university/course-builder"
                  className="rounded-full px-4 py-2 text-sm font-medium"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid="fd-open-course-builder">
              Open Course Builder →
            </Link>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-8">
          {SEGMENT_ORDER.map(([key, label, color]) => (
            <div key={key} className="rounded-xl p-4 border" style={{ borderColor: PAL.border, background: PAL.panel }}
                 data-testid={`fd-stat-${key}`}>
              <div className="text-[10px] uppercase tracking-widest" style={{ color }}>{label}</div>
              <div className="text-3xl font-semibold mt-1">{(data?.segments?.[key] || []).length}</div>
            </div>
          ))}
        </div>

        {/* Pending jobs strip */}
        {data?.pending_jobs?.length > 0 && (
          <div className="mt-8 rounded-2xl p-5 border" style={{ borderColor: PAL.border, background: PAL.panel }}
               data-testid="fd-pending-jobs">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest" style={{ color: PAL.accent }}>
                Active AI jobs ({data.pending_jobs.length})
              </div>
              <Link to="/kcis/admin/job-center" className="text-xs" style={{ color: PAL.accent }}>
                Open Job Center →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.pending_jobs.slice(0, 6).map((j) => (
                <div key={j.job_id} className="text-xs flex items-center gap-2 p-2 rounded"
                     style={{ background: "#0F0B14" }}>
                  <span className="font-mono text-[10px]" style={{ color: PAL.muted }}>{j.job_type}</span>
                  <span className="flex-1">{j.current_step}</span>
                  <span style={{ color: PAL.accent }}>{j.progress_percent}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Segmented lists */}
        <div className="mt-8 space-y-6">
          {SEGMENT_ORDER.map(([key, label, color]) => {
            const items = data?.segments?.[key] || [];
            if (!items.length) return null;
            return (
              <div key={key} data-testid={`fd-segment-${key}`}>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ color }}>{label}</div>
                <div className="space-y-2">
                  {items.map((c) => {
                    const p = profiles.find((x) => x.id === c.primary_instructor_id);
                    return (
                      <div key={c.id}
                           className="rounded-xl p-4 border transition hover:-translate-y-0.5"
                           style={{ borderColor: PAL.border, background: PAL.panel }}
                           data-testid={`fd-course-${c.id}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Link to={`/kcis/admin/university/course-builder/${c.id}`}
                                className="font-medium hover:underline">{c.title}</Link>
                          <span className="text-xs" style={{ color: PAL.muted }}>{c.course_code}</span>
                          {c.podcast_ready && (
                            <span className="text-[10px] uppercase px-2 py-0.5 rounded"
                                  style={{ background: "#7C3AED", color: "white" }}>Podcast</span>
                          )}
                          <div className="ml-auto">
                            <PreviewAsStudentButton courseId={c.id}
                                                       variant="ghost"
                                                       testIdSuffix={c.id} />
                          </div>
                        </div>
                        <div className="text-xs mt-1" style={{ color: PAL.muted }}>
                          Instructor: {p?.identity?.full_name || "Default (Dr. Christy Vonta)"} · {c.module_count} modules · {c.lesson_count} lessons · {c.quiz_count} quizzes
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {podcastReady.length > 0 && (
            <div data-testid="fd-segment-podcast_ready">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "#7C3AED" }}>
                Podcast Ready ({podcastReady.length})
              </div>
              <div className="space-y-2">
                {podcastReady.map((c) => (
                  <Link key={c.id} to={`/kcis/admin/university/course-builder/${c.id}`}
                        className="block rounded-xl p-4 border" style={{ borderColor: PAL.border, background: PAL.panel }}
                        data-testid={`fd-podcast-${c.id}`}>
                    <div className="font-medium">{c.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {data && data.total === 0 && (
            <div className="rounded-xl p-6 border" style={{ borderColor: PAL.border, background: PAL.panel }}>
              <div className="text-sm" style={{ color: PAL.muted }}>
                No courses yet. Head to the Course Builder to start your first draft.
              </div>
            </div>
          )}
        </div>
      </div>
    </KcisAdminShell>
  );
}
